import React, { useContext } from "react";
import SidebarTreeItem from "./SidebarTreeItem";
import EventList from "./EventList";
import ResizableHandle from "./ResizableHandle";
import CreateDropdownMenu from "./CreateDropdownMenu";
import { EventType, Album, EnhancedFolder } from "./types";
import { PhotoManagerContext } from "./index";

const Library = require("../styles/images/Library.svg");
const Add = require("../styles/images/add.svg");

interface PhotoManagerSidebarProps {
  // Only callback props that contain business logic not in context
  handleFolderClickInSidebar: (
    folder: EnhancedFolder,
    encodedPath: string
  ) => void;
  handleEventClickInSidebar: (event: EventType, encodedPath: string) => void;
  handleAlbumClick: (album: Album, encodedPath: string, event: EventType) => void;
  jumpToContentView: () => void;
  getFolderPath: (
    folder: EnhancedFolder,
    rootFolders: EnhancedFolder[]
  ) => string;
  handlePageChange: (type: "event" | "photo", page: number) => void;
  loadMore: () => void;
  toggleHeaderCreateDropdown: () => void;
  handleHeaderGalleryClick: () => void;
  handleHeaderFolderClick: () => void;
}

const PhotoManagerSidebar: React.FC<PhotoManagerSidebarProps> = ({
  handleFolderClickInSidebar,
  handleEventClickInSidebar,
  handleAlbumClick,
  jumpToContentView,
  getFolderPath,
  handlePageChange,
  loadMore,
  toggleHeaderCreateDropdown,
  handleHeaderGalleryClick,
  handleHeaderFolderClick
}) => {
  const context = useContext(PhotoManagerContext);

  if (!context) return null;

  const {
    sidebarWidth,
    isMobile,
    invitedEventsMode,
    organizationName,
    showHeaderCreateDropdown,
    rootFolders,
    openedFolders,
    selectedFolderId,
    folderLoadingId,
    encodedString,
    eventsList,
    openedEvents,
    selectedEvent,
    eventListCurrentPage,
    eventListNextPage,
    eventsLoading,
    albums,
    selectedAlbum,
    albumLoadingEventId,
    showActionTooltip,
    generateEventInstanceId,
    isEventInstanceExpanded,
    handleEventCollapse,
    handleFolderCollapse,
    handleURLChange,
    setEncodedString,
    setShowNewAlbumModal,
    handleSidebarResize
  } = context;
  return (
    <div className="geo-manager-sidebar" style={{ width: `${sidebarWidth}px` }}>
      <div className="geo-manager-header">
        <div>
          <a
            className="geo-manager-ptitle"
            href={invitedEventsMode ? "/library?invited_events=true" : "/library"}
          >
            <img src={Library} className="ws-lib-icon" alt="library-icon" />
            {sidebarWidth > 120 && (
              <span className="geo-manager-ptitle-text">
                {!invitedEventsMode && organizationName && (
                  <span className="geo-manager-org-name">{organizationName}</span>
                )}
                <span className="geo-manager-page-title">
                  {invitedEventsMode ? "Invited Events" : "Library"}
                </span>
              </span>
            )}
          </a>
        </div>
        {sidebarWidth > 120 && !invitedEventsMode && context.canCreateRootContent && (
          <div className="ws-header-icon-container">
            <div className="dropdown">
              <button
                type="button"
                id={`lib-icon`}
                onClick={toggleHeaderCreateDropdown}
                aria-haspopup="true"
                aria-expanded={showHeaderCreateDropdown}
              >
                <img
                  src={Add}
                  className="ws-add-event-icon"
                  alt="create-event-icon"
                />
              </button>

              {showHeaderCreateDropdown && (
                <CreateDropdownMenu
                  onGalleryClick={handleHeaderGalleryClick}
                  onFolderClick={handleHeaderFolderClick}
                  canCreateEvents={context.canCreateRootContent}
                  canCreateFolders={context.canCreateRootContent}
                />
              )}
            </div>
          </div>
        )}
      </div>
      {sidebarWidth > 120 && (
        <div className="geo-manager-sidebar-content">
          <div className="ws-sidebar-tree geo-flex">
            <ul>
              {!isMobile &&
                rootFolders.map(folder => (
                  <SidebarTreeItem
                    key={getFolderPath(folder, rootFolders)}
                    folder={folder}
                    onFolderClick={handleFolderClickInSidebar}
                    onEventClick={handleEventClickInSidebar}
                    albums={albums}
                    onAlbumClick={handleAlbumClick}
                    openedEvents={openedEvents}
                    generateEventInstanceId={generateEventInstanceId}
                    isEventInstanceExpanded={isEventInstanceExpanded}
                    onEventCollapse={handleEventCollapse}
                    selectedAlbum={selectedAlbum}
                    selectedEvent={selectedEvent}
                    selectedFolderId={selectedFolderId}
                    albumLoadingEventId={albumLoadingEventId}
                    setShowNewAlbumModal={setShowNewAlbumModal}
                    isMobile={isMobile}
                    jumpToContentView={jumpToContentView}
                    openedFolders={openedFolders}
                    onFolderCollapse={handleFolderCollapse}
                    getFolderPath={getFolderPath}
                    folderLoadingId={folderLoadingId}
                    handleURLChange={handleURLChange}
                    libraryRootFolders={rootFolders}
                    setEncodedString={setEncodedString}
                    currentEncodedPath={encodedString}
                  />
                ))}
              {eventsList.length > 0 && (
                <EventList
                  events={eventsList}
                  handleEventClick={handleEventClickInSidebar}
                  handleEventCollapse={handleEventCollapse}
                  albums={albums}
                  openedEvents={openedEvents}
                  generateEventInstanceId={generateEventInstanceId}
                  isEventInstanceExpanded={isEventInstanceExpanded}
                  handleAlbumClick={handleAlbumClick}
                  selectedAlbum={selectedAlbum}
                  selectedEvent={selectedEvent}
                  jumpToContentView={jumpToContentView}
                  isMobile={isMobile}
                  currentPage={eventListCurrentPage}
                  handlePageChange={(page: number) =>
                    handlePageChange("event", page)
                  }
                  nextPage={eventListNextPage}
                  loadMore={loadMore}
                  albumLoadingEventId={albumLoadingEventId}
                  eventsLoading={eventsLoading}
                  setShowNewAlbumModal={setShowNewAlbumModal}
                  rootFolders={rootFolders}
                  handleFolderClickInSidebar={handleFolderClickInSidebar}
                  onFolderCollapse={handleFolderCollapse}
                  getFolderPath={getFolderPath}
                  openedFolders={openedFolders}
                  folderLoadingId={folderLoadingId}
                  handleURLChange={handleURLChange}
                  folderId={null}
                  setEncodedString={setEncodedString}
                  currentEncodedPath={encodedString}
                  libraryRootFolders={rootFolders}
                />
              )}
            </ul>
          </div>
        </div>
      )}
      <ResizableHandle
        onResize={handleSidebarResize}
        minWidth={220}
        maxWidth={700}
        currentWidth={sidebarWidth}
      />
    </div>
  );
};

export default PhotoManagerSidebar;
